// Shared configuration for Dashboard and MyInfo categories
import { DASHBOARD_TYPES } from '@/common/constants';

export const ACADEMIC_EXCELLENCE_OPTIONS = {
  title: 'Academics',
  description: 'Manage course requirements and track progress.',
  icon: 'school',
  category: 'academic',
  items: [
    {
      id: 'upload-docs',
      title: 'Upload Documents',
      description: 'Securely submit necessary documents and credentials.',
      redirectTo: 'std-manage-records',
      icon: 'drive_folder_upload',
      btnText: 'Upload',
      abbr: 'uploaded_docs',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
    },
    {
      id: 'manage-payments',
      title: 'Make Payments',
      description: 'Track payment history and view installment plans.',
      redirectTo: 'std-payment-list',
      icon: 'payment',
      btnText: 'View',
      abbr: 'payments_paid',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
      // Note: This page should have "My Installment Plan" button that redirects to manage-plans
    },
    {
      id: 'view-courses',
      title: 'View Courses',
      description: 'Enroll in courses to learn new skills.',
      redirectTo: 'student-lms-courses',
      icon: 'book',
      btnText: 'View',
      abbr: 'courses',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL, DASHBOARD_TYPES.CARECAMPUS],
    },
    {
      id: 'start-discussion',
      title: 'Discussion Board',
      description: 'Review your inquiries and responses for continuous learning.',
      redirectTo: 'student-questions',
      icon: 'help',
      btnText: 'View',
      abbr: 'ask_questions',
      requiresPayment: true,
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
    },
    {
      id: 'view-resources',
      title: 'View Resources',
      description: 'Access a wide range of resources to support your career development.',
      redirectTo: 'student-community',
      icon: 'folder',
      btnText: 'View',
      abbr: 'resources',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL, DASHBOARD_TYPES.CARECAMPUS],
    },
    {
      id: 'manage-attendance',
      title: 'Schedule and Attendance',
      description: 'See your class schedule and attendance records.',
      redirectTo: 'student-attendance',
      icon: 'calendar_today',
      btnText: 'View',
      abbr: 'manage_attendance',
      requiresPayment: true,
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
    },
    
    {
      id: 'program-certificate',
      title: 'Download Program Certificate',
      description: 'Download your program certificate for the registered course.',
      icon: 'receipt',
      btnText: 'Download',
      btnIcon: 'file_download',
      abbr: 'program_certificate',
      isAction: true,
      requiresPayment: true,
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
    },
    {
      id: 'manage-exam-results',
      title: 'Manage Exam Results',
      description: 'Submit and track your credential exam results.',
      redirectTo: 'student-exam-results',
      icon: 'assignment_turned_in',
      btnText: 'Manage',
      abbr: 'exam_results',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
    }
  ],
};

export const SKILLS_DEVELOPMENT_OPTIONS = {
  title: 'Skills',
  description: 'Build, practice, and assess your clinical skills.',
  icon: 'healing',
  category: 'skills_development',
  items: [
    {
      id: 'view-skill-library',
      title: 'View Skill Library',
      description: 'Explore skills library to enhance your knowledge.',
      redirectTo: 'student-library',
      icon: 'menu_book',
      btnText: 'Explore',
      abbr: 'library',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
    },
    {
      id: 'ai-skill-cards',
      title: 'AI Skill Cards',
      description: 'View and manage your skill cards.',
      redirectTo: 'std-profile-skillcard',
      icon: 'map',
      btnText: 'View',
      abbr: 'skillmaps',
      requiresAISkills: true,
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
    },
    {
      id: 'skillform-games',
      title: 'Skillform Games',
      description: 'Play interactive games to build your skills.',
      redirectTo: 'student-skillform-games',
      icon: 'sports_esports',
      btnText: 'Play',
      abbr: 'skillform_games',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
    },
    {
      id: 'skillform-exams',
      title: 'Exam Simulator',
      description: 'Practice with realistic exam simulations.',
      redirectTo: 'student-skillform-exams',
      icon: 'assignment',
      btnText: 'Practice',
      abbr: 'skillform_exams',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
    },
    {
      id: 'skillform-flashcards',
      title: 'Flashcards',
      description: 'Study with interactive flashcards.',
      redirectTo: 'student-skillform-flashcards',
      icon: 'style',
      btnText: 'Study',
      abbr: 'skillform_flashcards',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
    },
    {
      id: 'skill-simulation',
      title: 'Skill Simulation',
      description: 'Practice skills using video simulation.',
      redirectTo: 'student-simulations-listing',
      icon: 'videocam',
      btnText: 'Practice',
      abbr: 'skills',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
    },
  ],
};

export const CAREER_OPPORTUNITIES_OPTIONS = {
  title: 'Careers',
  description: 'Explore companies, jobs, and sponsorship opportunities.',
  icon: 'work_outline',
  category: 'career_opportunities',
  items: [
    {
      id: 'find-companies',
      title: 'Find Companies',
      description: 'Discover employers actively seeking new talent.',
      redirectTo: 'student-employers-list',
      icon: 'business',
      btnText: 'Find',
      abbr: 'find_companies',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL, DASHBOARD_TYPES.CARECAMPUS],
    },
    {
      id: 'manage-job-offers',
      title: 'Manage Job Offers',
      description: 'View all added offers with option to add new job offers.',
      redirectTo: 'my-offers',
      icon: 'card_giftcard',
      btnText: 'Manage',
      abbr: 'add_new_offers',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL, DASHBOARD_TYPES.ORGANIZATION],
      // Note: This page should have button to add new offers
    },
    {
      id: 'view-sponsorships',
      title: 'View Sponsorships',
      description: 'List of all sponsorships with button to view your applications.',
      redirectTo: 'student-scholarships-listing',
      icon: 'stars',
      btnText: 'View',
      abbr: 'sponsorships',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL, DASHBOARD_TYPES.CARECAMPUS],
      // Note: This page should have "My Applications" button that redirects to std-manage-applications
    },
    {
      id: 'job-search',
      title: 'Job Search',
      description: 'Find job opportunities that fit your experience.',
      redirectTo: 'student-jobs-listing',
      icon: 'search',
      btnText: 'Search',
      abbr: 'job_search',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL, DASHBOARD_TYPES.CARECAMPUS],
    },
    {
      id: 'career-pathways',
      title: 'Explore Career Pathways',
      description: 'Find universities and organizations that support your career goals.',
      redirectTo: 'student-careers',
      icon: 'local_hospital',
      btnText: 'Explore',
      abbr: 'career_pathways',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL, DASHBOARD_TYPES.CARECAMPUS],
    },
  ],
};

export const COMMUNITY_OPTIONS = {
  title: 'Community',
  description: 'Connect and grow with others',
  icon: 'people',
  category: 'community',
  items: [
    {
      id: 'discussion-board',
      title: 'Discussion Board',
      description: 'Participate in community discussions and connect with others.',
      redirectTo: 'org-questions-list',
      icon: 'forum',
      btnText: 'View',
      abbr: 'discussion_board',
      dashboardTypes: [DASHBOARD_TYPES.ORGANIZATION],
    },
  ],
};

export const CAREER_PREPARATION_OPTIONS = {
  title: 'Career Prep',
  description: 'Build skills and prepare for career success.',
  icon: 'trending_up',
  category: 'career_preparation',
  items: [
    {
      id: 'interview-preparation',
      title: 'Interview Preparation',
      description: 'Practice with role-based interviews and view your submissions.',
      redirectTo: 'student-simulations-listing',
      icon: 'mic',
      btnText: 'Practice',
      abbr: 'interviews',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL, DASHBOARD_TYPES.ORGANIZATION, DASHBOARD_TYPES.CARECAMPUS],
      // Note: This page should have "My Interviews" button that redirects to student-submissions-listing
    },
    {
      id: 'build-resume',
      title: 'Build A Resume',
      description: 'Build a professional resume to stand out in your field.',
      redirectTo: 'std-manage-resume',
      icon: 'description',
      btnText: 'Build',
      abbr: 'resume_build',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL, DASHBOARD_TYPES.ORGANIZATION, DASHBOARD_TYPES.CARECAMPUS],
    },
    {
      id: 'certifications',
      title: 'Manage Certifications',
      description: 'Showcase your certifications to demonstrate your skills and expertise.',
      redirectTo: 'my-certifications',
      icon: 'workspace_premium',
      btnText: 'Manage',
      abbr: 'add_certifications',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL, DASHBOARD_TYPES.ORGANIZATION, DASHBOARD_TYPES.CARECAMPUS],
    },
    {
      id: 'manage-research',
      title: 'Manage Research',
      description: 'Showcase your research to demonstrate your expertise and earn points.',
      redirectTo: 'my-research',
      icon: 'menu_book',
      btnText: 'Manage',
      abbr: 'add_new_research',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL, DASHBOARD_TYPES.ORGANIZATION, DASHBOARD_TYPES.CARECAMPUS],
    },
    {
      id: 'care-ready-documents',
      title: 'CareReady Wallet',
      description: 'Manage your ready-to-work CNA/CMA profile.',
      redirectTo: 'care-ready-documents',
      icon: 'drive_folder_upload',
      btnText: 'Review',
      abbr: 'care_ready_docs',
      dashboardTypes: [DASHBOARD_TYPES.SCHOOL],
    },
  ],
};

// Deprecated - keep for backward compatibility
// TransitionU bucket: Programs and Schools live under one Home card.
export const EDUCATION_OPTIONS = {
  title: 'Education',
  description: 'Compare programs and find schools for your next credential.',
  icon: 'school',
  category: 'education',
  items: [
    {
      id: 'tu-programs',
      title: 'Programs',
      description: 'Compare tuition, pass rates and application dates across schools.',
      redirectTo: 'student-programs',
      icon: 'menu_book',
      btnText: 'View',
      abbr: 'tu_programs',
      dashboardTypes: [DASHBOARD_TYPES.CARECAMPUS],
    },
    {
      id: 'tu-schools',
      title: 'Schools',
      description: 'Find schools offering the healthcare training you need.',
      redirectTo: 'student-institutions',
      icon: 'account_balance',
      btnText: 'View',
      abbr: 'tu_schools',
      dashboardTypes: [DASHBOARD_TYPES.CARECAMPUS],
    },
  ],
};

export const CAREERS_OPTIONS = CAREER_PREPARATION_OPTIONS;
export const OTHERS_OPTIONS = CAREER_OPPORTUNITIES_OPTIONS;

// Mapping of dashboard config abbr to category
export const ABBR_TO_CATEGORY = {
  // Academic Excellence
  uploaded_docs: 'academic',
  payments_paid: 'academic',
  courses: 'academic',
  ask_questions: 'academic',
  resources: 'academic',
  program_certificate: 'academic',
  manage_attendance: 'academic',
  exam_results: 'academic',

  // Skills Development
  library: 'skills_development',
  skills: 'skills_development',
  skillmaps: 'skills_development',
  skillform_games: 'skills_development',
  skillform_exams: 'skills_development',
  skillform_flashcards: 'skills_development',

  // Career Opportunities
  find_companies: 'career_opportunities',
  add_new_offers: 'career_opportunities',
  sponsorships: 'career_opportunities',
  job_search: 'career_opportunities',
  jobs: 'career_opportunities',
  research: 'career_opportunities',
  career_pathways: 'career_opportunities',

  // Career Preparation & Growth
  interviews: 'career_preparation',
  resume_build: 'career_preparation',
  care_ready_docs: 'career_preparation',
  add_certifications: 'career_preparation',
  add_new_research: 'career_preparation',
  practice_skill_simulations: 'career_preparation',

  // Community
  discussion_board: 'community',
};

// Icon overrides for items whose backend-stored icons are unavailable in this icon set
const ICON_OVERRIDES = {
  quiz: 'assignment',
  psychology: 'healing',
  science: 'menu_book',
};

// Helper function to categorize dashboard config items from API
export function categorizeDashboardItems(dashboardConfig) {
  const academic = [];
  const skills_development = [];
  const career_opportunities = [];
  const career_preparation = [];
  const community = [];

  if (!dashboardConfig || !Array.isArray(dashboardConfig)) {
    return { academic, skills_development, career_opportunities, career_preparation, community };
  }

  dashboardConfig.forEach(item => {
    if (!item.enable) return; // Skip disabled items

    // Fix unavailable icons stored in database
    if (item.icon && ICON_OVERRIDES[item.icon]) {
      item.icon = ICON_OVERRIDES[item.icon];
    }

    const category = ABBR_TO_CATEGORY[item.abbr] || 'academic';

    switch (category) {
      case 'academic':
        academic.push(item);
        break;
      case 'skills_development':
        skills_development.push(item);
        break;
      case 'career_opportunities':
        career_opportunities.push(item);
        break;
      case 'career_preparation':
        career_preparation.push(item);
        break;
      case 'community':
        community.push(item);
        break;
    }
  });

  return { academic, skills_development, career_opportunities, career_preparation, community };
}

// Helper function to filter items based on dashboard type and user settings
export function getCategoryItems(category, dashboardType, userSettings = {}) {
  const { requiresPayment = false, requiresInAppPayment = false, aiSkillsEnabled = false } = userSettings;

  return category.items.filter(item => {
    // Check dashboard type compatibility
    if (!item.dashboardTypes.includes(dashboardType)) {
      return false;
    }

    // Check payment requirements
    if (item.requiresPayment && !requiresPayment) {
      return false;
    }

    if (item.requiresInAppPayment && !requiresInAppPayment) {
      return false;
    }

    // Check AI skills requirement
    if (item.requiresAISkills && !aiSkillsEnabled) {
      return false;
    }

    return true;
  });
}

// Helper function to count tasks for a category
export function getCategoryTaskCount(category, dashboardType, userSettings = {}) {
  return getCategoryItems(category, dashboardType, userSettings).length;
}

// Helper function to count tasks for a category (from API config)
export function getCategoryTaskCountFromConfig(categorizedItems) {
  return categorizedItems.length;
}

// Helper function to merge API config with hardcoded items
// Uses API config for items that exist, supplements with hardcoded items for missing ones
export function mergeWithStaticConfig(apiItems, staticCategory, dashboardType, userSettings = {}) {
  // Whitelist of items to hardcode (not managed by API config)
  const hardcodedWhitelist = [
    'ask_questions', // Ask Questions
    'program_certificate', // Download Program Certificate
    'manage_attendance', // Manage Attendance
    'exam_results', // Manage Exam Results
    'add_certifications', // Manage Certifications
    'care_ready_docs',
    'add_new_research', // Manage Research
    // 'add_new_offers',          // Manage Job Offers
     // Practice Skill Simulations
     // Skill Library
    'skillmaps', // AI Skill Cards
  ];

  // Get all static items that should be shown
  const staticItems = getCategoryItems(staticCategory, dashboardType, userSettings);

  // Build lookup of static items by abbr for icon/redirectTo overrides
  const staticByAbbr = {};
  staticItems.forEach(item => { staticByAbbr[item.abbr] = item; });

  // Override API item icons with static config (source of truth for icons)
  const enrichedApiItems = apiItems.map(item => {
    const staticItem = staticByAbbr[item.abbr];
    if (staticItem) {
      return { ...item, icon: staticItem.icon, redirectTo: staticItem.redirectTo };
    }
    return item;
  });

  const apiAbbrs = new Set(apiItems.map(item => item.abbr));

  // Find static items that are NOT in the API config AND are in the whitelist
  const missingItems = staticItems.filter(item => !apiAbbrs.has(item.abbr) && hardcodedWhitelist.includes(item.abbr));

  // Combine API items with missing static items
  return [...enrichedApiItems, ...missingItems];
}
